import React, { useState, useRef, useMemo } from "react";
import JoditEditor from "jodit-react";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { FiX, FiBookOpen } from "react-icons/fi";

export default function GuidelineModal({ data, onClose, onSuccess }) {
  const editor = useRef(null);

  const [content, setContent] = useState(data?.guildelines || "");
  const [loading, setLoading] = useState(false);

  // Editor configuration
  const config = useMemo(
    () => ({
      readonly: false,
      placeholder: "Start typing guidelines here...",
      height: 350,
      toolbarAdaptive: false,
      buttons: [
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "|",
        "ul",
        "ol",
        "|",
        "font",
        "fontsize",
        "brush",
        "paragraph",
        "|",
        "table",
        "link",
        "|",
        "align",
        "undo",
        "redo",
        "|",
        "hr",
        "eraser",
        "fullsize",
      ],
    }),
    [],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await makeRequestAuth("post", "/admin/updatePaper", {
        paperId: data._id,
        paperName: data.paperName || data.name,
        totalQuestions: Number(data.totalQuestions),
        durationMinutes: Number(data.durationMinutes),
        guildelines: content,
      });

      showSuccessToast("Guidelines saved successfully");
      onSuccess();
    } catch (error) {
      showErrorToast(
        error?.response?.data?.message || "Failed to save guidelines",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in duration-200">
        {/* HEADER - Indigo Theme */}
        <div className="flex justify-between items-center px-6 py-4 border-b bg-indigo-50/50">
          <div className="flex items-center gap-2">
            <div className="bg-indigo-100 p-2 rounded-lg text-indigo-600">
              <FiBookOpen size={20} />
            </div>
            <h3 className="text-lg font-bold text-gray-800">
              Paper Guidelines
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 rounded-full transition-colors"
          >
            <FiX size={22} />
          </button>
        </div>

        {/* BODY */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Instructions for Students
            </label>
            <div className="border rounded-xl overflow-hidden">
              <JoditEditor
                ref={editor}
                value={content}
                config={config}
                onBlur={(newContent) => setContent(newContent)}
              />
            </div>
          </div>

          {/* FOOTER */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-200 disabled:opacity-50 transition-all active:scale-95"
            >
              {loading ? "Saving..." : "Save Guidelines"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
